import { computed, DestroyRef, effect, inject, Injectable, Signal, signal, TemplateRef, Type } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { ToolbarItem } from '@services/popup-helper.service';
import { LoggerService } from '@services/logger.service';

/** Ab dieser Bildschirmbreite kann das Panel angeheftet werden. */
const LARGE_SCREEN_QUERY = '(min-width: 1024px)';

/** Zusätzlicher Button des Inhalts im Kopfbereich (vor Anheften und Schließen). */
export interface ISidePanelHeaderButton {
  icon: string;
  hint: string;
  onClick: () => void;
}

/** Kennzeichnung des Inhalts neben dem Titel (z. B. Workflow-Status); nicht bedienbar. */
export interface ISidePanelTitleBadge {
  text: string;
  /** Material-Icon vor dem Text. */
  icon: string;
  /** CSS-Klasse für die Farbe, z. B. `types-selectbox_background-normal`. */
  colorClass: string;
}

export interface ISidePanelOptions {
  /** Titel im Kopfbereich des Panels. */
  title: string;
  /** Werte für die Inputs der Inhaltskomponente (nur bei Komponenten-Inhalten). */
  inputs?: Record<string, unknown>;
  /**
   * Items des Fußbereichs, solange dieser Inhalt aktiv ist. Komponenten-Inhalte können ihre Items
   * stattdessen selbst über `connectFooterItems` verbinden.
   */
  footerItems?: Signal<ToolbarItem[]>;
  /** Genau ein zusätzlicher Button im Kopfbereich; `null` blendet ihn aus. */
  headerButton?: Signal<ISidePanelHeaderButton | null>;
  /** Genau eine Kennzeichnung neben dem Titel; `null` blendet sie aus. */
  titleBadge?: Signal<ISidePanelTitleBadge | null>;
  /**
   * Schließabfrage des Inhalts; `false` lehnt das Schließen ab. Wird beim Schließen-Button, beim
   * Routenwechsel und beim Ersetzen durch einen anderen Inhalt gefragt.
   */
  canClose?: () => Promise<boolean>;
  /** Wird genau einmal aufgerufen, wenn dieser Inhalt das Panel verlässt (geschlossen oder ersetzt). */
  onClosed?: () => void;
}

export interface ISidePanelContent extends ISidePanelOptions {
  /** Komponente des Inhalts; `null` bei Template-Inhalten. */
  component: Type<unknown> | null;
  /** Template des Inhalts; `null` bei Komponenten-Inhalten. */
  template: TemplateRef<unknown> | null;
}

/** Inhalt, dessen Schließabfrage für eine laufende Navigation zugestimmt hat. */
interface IApprovedNavigation {
  navigationId: number;
  content: ISidePanelContent;
}

/**
 * Steuert das rechte Seitenpanel der Application-Shell. Es zeigt immer höchstens einen Inhalt;
 * ein neu geöffneter Inhalt ersetzt den bisherigen.
 */
@Injectable({ providedIn: 'root' })
export class SidePanelService {
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService);

  readonly #content = signal<ISidePanelContent | null>(null);
  /** Bleibt über das Schließen hinaus erhalten und gilt für die ganze Sitzung (bis zum Neuladen). */
  readonly #pinned = signal(false);
  readonly #footerItemsSource = signal<Signal<ToolbarItem[]> | null>(null);

  /** Laufende Schließabfrage; verhindert doppelte Abfragen desselben Inhalts. */
  #pendingCanClose: { content: ISidePanelContent; result: Promise<boolean> } | null = null;
  /** Vom Navigations-Guard gesetzt, ausgewertet bei `NavigationEnd`. */
  #approvedNavigation: IApprovedNavigation | null = null;

  readonly content = this.#content.asReadonly();
  readonly pinned = this.#pinned.asReadonly();

  /** Items des Fußbereichs; Items für die obere Leiste (`toolbar: 'top'`) werden nicht angezeigt. */
  readonly footerItems = computed(() => (this.#activeFooterItems()?.() ?? []).filter(item => item.toolbar !== 'top'));

  /** Zusätzlicher Kopf-Button des aktiven Inhalts oder `null`. */
  readonly headerButton = computed(() => this.#content()?.headerButton?.() ?? null);

  /** Kennzeichnung neben dem Titel des aktiven Inhalts oder `null`. */
  readonly titleBadge = computed(() => this.#content()?.titleBadge?.() ?? null);

  readonly isLargeScreen = toSignal(
    this.breakpointObserver.observe(LARGE_SCREEN_QUERY).pipe(map(result => result.matches)),
    { initialValue: window.matchMedia(LARGE_SCREEN_QUERY).matches }
  );

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(event => this.#onNavigationEnd(event));

    // Wird der Bildschirm zu schmal, wird das Panel gelöst und bleibt auch danach gelöst.
    effect(() => {
      if (!this.isLargeScreen()) {
        this.#pinned.set(false);
      }
    });
  }

  /**
   * Öffnet einen Inhalt (Komponente oder Template) und ersetzt den bisherigen. Lehnt der bisherige
   * Inhalt das Schließen ab, bleibt er geöffnet und das Ergebnis ist `false`. Ohne Schließabfrage
   * des bisherigen Inhalts wird sofort (synchron) ersetzt.
   */
  async open(content: Type<unknown> | TemplateRef<unknown>, options: ISidePanelOptions): Promise<boolean> {
    let current = this.#content();
    while (current?.canClose) {
      if (!(await this.#askCanClose(current))) {
        return false;
      }
      // Während der Abfrage kann sich der aktive Inhalt geändert haben.
      if (this.#content() === current) {
        break;
      }
      current = this.#content();
    }

    const isTemplate = content instanceof TemplateRef;
    this.#replaceContent({
      ...options,
      component: isTemplate ? null : content,
      template: isTemplate ? content : null
    });
    return true;
  }

  /**
   * Schließt das Panel ohne Schließabfrage. Mit Angabe des Inhalts (Komponente bzw. Template wie
   * bei `open()`) wird nur geschlossen, wenn dieser Inhalt noch aktiv ist.
   */
  close(content?: Type<unknown> | TemplateRef<unknown>): void {
    const current = this.#content();
    if (content && (!current || (current.component ?? current.template) !== content)) {
      return;
    }
    this.#footerItemsSource.set(null);
    // Zuletzt, damit ein in `onClosed` neu geöffneter Inhalt nicht zurückgesetzt wird.
    this.#replaceContent(null);
  }

  /**
   * Schließt das Panel nach Zustimmung der Schließabfrage des aktiven Inhalts (Schließen-Button).
   * Liefert `false`, wenn der Inhalt das Schließen ablehnt.
   */
  async requestClose(): Promise<boolean> {
    const content = this.#content();
    if (!content) {
      return true;
    }
    if (content.canClose && !(await this.#askCanClose(content))) {
      return false;
    }
    // Während der Abfrage kann der Inhalt bereits ersetzt oder geschlossen worden sein.
    if (this.#content() === content) {
      this.close();
    }
    return true;
  }

  /**
   * Schließabfrage für den Navigations-Guard (`canDeactivateSidePanelGuard`): fragt nur den
   * aktiven Inhalt, schließt aber nicht. Das Schließen erfolgt bei `NavigationEnd`.
   */
  async canDeactivate(): Promise<boolean> {
    const content = this.#content();
    if (!content?.canClose) {
      return true;
    }
    const navigationId = this.router.currentNavigation()?.id;
    const allowed = await this.#askCanClose(content);
    if (allowed && navigationId !== undefined) {
      this.#approvedNavigation = { navigationId, content };
    }
    return allowed;
  }

  /**
   * Verbindet die Items des Fußbereichs mit dem Inhalt; Änderungen des Signals werden sofort
   * angezeigt. Muss im Injection-Kontext der Inhaltskomponente (z. B. im Konstruktor) aufgerufen
   * werden; beim Zerstören der Komponente wird die Verbindung gelöst.
   */
  connectFooterItems(items: Signal<ToolbarItem[]>): void {
    this.#footerItemsSource.set(items);
    inject(DestroyRef).onDestroy(() => {
      // Ein nachfolgender Inhalt kann sich bereits verbunden haben.
      if (this.#footerItemsSource() === items) {
        this.#footerItemsSource.set(null);
      }
    });
  }

  togglePin(): void {
    this.#pinned.update(pinned => !pinned && this.isLargeScreen());
  }

  /** Quelle der Fuß-Items: Angabe beim Öffnen, sonst (nur Komponenten) die verbundene Quelle. */
  #activeFooterItems(): Signal<ToolbarItem[]> | null {
    const content = this.#content();
    if (!content) {
      return null;
    }
    return content.footerItems ?? (content.template ? null : this.#footerItemsSource());
  }

  /** Setzt den aktiven Inhalt; der verlassene Inhalt erhält genau einmal `onClosed`. */
  #replaceContent(next: ISidePanelContent | null): void {
    const previous = this.#content();
    this.#content.set(next);
    if (previous && previous !== next) {
      previous.onClosed?.();
    }
  }

  #askCanClose(content: ISidePanelContent): Promise<boolean> {
    if (this.#pendingCanClose?.content === content) {
      return this.#pendingCanClose.result;
    }
    const result = new Promise<boolean>(resolve => resolve(content.canClose?.() ?? true))
      .catch((error: unknown) => {
        this.logger.error('Fehler bei der Schließabfrage des Seitenpanels', { error });
        return false;
      })
      .finally(() => {
        if (this.#pendingCanClose?.result === result) {
          this.#pendingCanClose = null;
        }
      });
    this.#pendingCanClose = { content, result };
    return result;
  }

  #onNavigationEnd(event: NavigationEnd): void {
    const content = this.#content();
    const approved = this.#approvedNavigation;
    this.#approvedNavigation = null;
    // Lief der Guard für diese Navigation nicht (z. B. nur Query-Parameter geändert), bleibt ein
    // Inhalt mit Schließabfrage offen, damit keine Änderungen verloren gehen.
    const isApproved = approved?.navigationId === event.id && approved.content === content;
    if (content?.canClose && !isApproved) {
      return;
    }
    this.close();
  }
}
